import { addDays } from 'date-fns';
import type { CompanyVacationRule, DayEntry, WageType } from '../types';

/**
 * Default-Note, die mergeCompanyVacationIntoEntries auf Tage ohne manuelle
 * Notiz setzt. Wird aus AppContext gegengelesen, um beim Entfernen einer
 * Betriebsferien-Regel nur die maschinell gesetzte Notiz zu löschen und
 * User-Notizen zu behalten.
 */
export const COMPANY_VACATION_DEFAULT_NOTE = 'Betriebsferien';

/**
 * Prüft ob ein Betriebsferien-Tag geblockt (nicht klickbar) sein soll.
 * Nur FIXED-User haben geblockte Betriebsferien. HOURLY/null/undefined = klickbar.
 */
export function isBlockedCompanyVacation(
  isCompanyVacation: boolean | undefined,
  wageType: WageType | null | undefined,
): boolean {
  return !!isCompanyVacation && wageType === 'FIXED';
}

/**
 * Berechnet alle Betriebsferien-Tage für ein gegebenes Jahr.
 * Unterstützt jahresübergreifende Zeiträume (z.B. 24.12. - 01.01.).
 * Gibt ein Set von ISO-Datums-Strings zurück (YYYY-MM-DD).
 */
export function getCompanyVacationDates(rule: CompanyVacationRule, year: number): Set<string> {
  const dates = new Set<string>();

  if (!rule.active) return dates;

  // Teil 1: Betriebsferien die im betrachteten Jahr STARTEN
  const startDate = new Date(year, rule.startMonth - 1, rule.startDay);
  const endYear = rule.endMonth < rule.startMonth ? year + 1 : year;
  const endDate = new Date(endYear, rule.endMonth - 1, rule.endDay);

  addWorkdaysInRange(startDate, endDate, year, dates);

  // Teil 2: Bei jahresübergreifend auch den Teil aus dem VORJAHR berücksichtigen
  // (z.B. wenn year=2026 und Regel 24.12.-01.01., dann auch 01.01.2026 aus dem Vorjahres-Zeitraum)
  if (rule.endMonth < rule.startMonth) {
    const prevStartDate = new Date(year - 1, rule.startMonth - 1, rule.startDay);
    const prevEndDate = new Date(year, rule.endMonth - 1, rule.endDay);

    addWorkdaysInRange(prevStartDate, prevEndDate, year, dates);
  }

  return dates;
}

function addWorkdaysInRange(start: Date, end: Date, filterYear: number, dates: Set<string>): void {
  // addDays ist DST-sicher (setDate(+1) kann beim DST-Uebergang 25h/23h
  // Offset ergeben und die <=-Bedingung fruehzeitig beenden).
  let current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6 && current.getFullYear() === filterYear) {
      const iso = formatDateISO(current);
      dates.add(iso);
    }
    current = addDays(current, 1);
  }
}

function formatDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Mergt Betriebsferien in bestehende Entries, differenziert nach WageType.
 *
 * - FIXED: isCompanyVacation + isVacation (erzwungener Urlaub, wie bisher)
 * - HOURLY/null: isCompanyVacation ohne isVacation (gesperrter Tag, kein Urlaubsabzug).
 *   Bestehende isVacation/isSickDay/isHalfVacation bleiben erhalten (manuell gesetzt).
 * - Feiertage: isVacation wird nie gesetzt (unabhängig vom WageType).
 */
export function mergeCompanyVacationIntoEntries(
  entries: Record<string, DayEntry>,
  vacationDates: Set<string>,
  wageType: WageType | null,
): Record<string, DayEntry> {
  const result = { ...entries };
  const isFixed = wageType === 'FIXED';

  vacationDates.forEach((date) => {
    const existing = result[date];

    if (isFixed) {
      // FIXED: Betriebsferien = erzwungener Urlaub (außer Feiertag)
      result[date] = {
        date,
        hours: 0,
        isHoliday: existing?.isHoliday || false,
        isVacation: !existing?.isHoliday,
        isCompanyVacation: true,
        note: existing?.note || COMPANY_VACATION_DEFAULT_NOTE,
        managerNote: existing?.managerNote,
        managerNoteAuthorId: existing?.managerNoteAuthorId,
        managerNoteAuthorName: existing?.managerNoteAuthorName,
        managerNoteTimestamp: existing?.managerNoteTimestamp,
      };
    } else {
      // HOURLY / null: Betriebsferien = gesperrter Tag, kein erzwungener Urlaub.
      // Bestehende manuelle Einträge (Urlaub, Krankheit) beibehalten.
      result[date] = {
        date,
        hours: 0,
        isHoliday: existing?.isHoliday || false,
        isVacation: existing?.isVacation || false,
        isHalfVacation: existing?.isHalfVacation || false,
        isCompanyVacation: true,
        isSickDay: existing?.isSickDay || false,
        note: existing?.note || COMPANY_VACATION_DEFAULT_NOTE,
        managerNote: existing?.managerNote,
        managerNoteAuthorId: existing?.managerNoteAuthorId,
        managerNoteAuthorName: existing?.managerNoteAuthorName,
        managerNoteTimestamp: existing?.managerNoteTimestamp,
      };
    }
  });

  return result;
}
